/**
 * Utility for generating stable React keys for list items
 * Uses simple hash function for performance and stability
 */

/**
 * Simple hash function for generating stable keys from content
 * Based on djb2 algorithm - fast and has good distribution
 */
const simpleHash = (str: string): string => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
  }
  return Math.abs(hash).toString(36);
};

/**
 * Generate a stable key for a list item based on content
 * @param prefix - A descriptive prefix for the key type
 * @param content - The content to generate a key for
 * @param index - Fallback index if content is empty (optional)
 * @returns A stable, unique key string
 */
export const generateListKey = (prefix: string, content: string, index?: number): string => {
  if (!content || content.trim() === '') {
    // Fallback to index if content is empty
    return `${prefix}-${index ?? 'empty'}`;
  }
  
  // Use content hash for stable, unique keys
  const hash = simpleHash(content.trim());
  return `${prefix}-${hash}`;
};

/**
 * Generate a key for CTA elements based on text and action
 * @param cta - CTA object with text and optional action
 * @param index - Fallback index
 * @returns A stable key
 */
export const generateCTAKey = (cta: { text: string; action?: string }, index: number): string => {
  const identifier = cta.action ? `${cta.text}-${cta.action}` : cta.text;
  return generateListKey('cta', identifier, index);
};

export default generateListKey;
